import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import {
  ArrowLeft, Calendar, Clock, Camera, Mic, FileText, TrendingDown, TrendingUp,
  ChevronRight, X, Sparkles, Activity,
} from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import Layout from "../components/Layout";

const COLOR_DOT = {
  green: "bg-sage-500 text-white ring-sage-500/30",
  amber: "bg-amber-500 text-white ring-amber-500/30",
  rose:  "bg-rose-500 text-white ring-rose-500/30",
  gray:  "bg-slate-300 text-slate-700 ring-slate-300/30",
};
const COLOR_LABEL = {
  green: "Improving",
  amber: "Holding steady",
  rose:  "Setback",
  gray:  "Baseline",
};

function MiniTrend({ data, color, label, suffix, yKey, invertColor = false }) {
  if (!data || data.length === 0) {
    return (
      <div className="bg-white border border-paper-200 rounded-2xl p-4 sm:p-5">
        <div className="label-overline mb-2">{label}</div>
        <div className="text-sm text-ink-500">No data yet — appears once you record outcomes.</div>
      </div>
    );
  }
  const last = data[data.length - 1][yKey];
  const first = data[0][yKey];
  const delta = (typeof last === "number" && typeof first === "number") ? (last - first) : null;
  // For pain, lower is better → invertColor=true so negative delta is green
  const isImprovement = delta !== null && (invertColor ? delta < 0 : delta > 0);
  const isWorsening = delta !== null && (invertColor ? delta > 0 : delta < 0);
  return (
    <div className="bg-white border border-paper-200 rounded-2xl p-4 sm:p-5" data-testid={`trend-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="label-overline">{label}</div>
        {delta !== null && (
          <span className={`text-xs font-medium inline-flex items-center gap-1 ${isImprovement ? "text-sage-700" : isWorsening ? "text-rose-600" : "text-ink-500"}`}>
            {isImprovement ? <TrendingUp className="w-3 h-3" /> : isWorsening ? <TrendingDown className="w-3 h-3" /> : null}
            {delta > 0 ? "+" : ""}{delta.toFixed(1)}{suffix}
          </span>
        )}
      </div>
      <div className="text-2xl font-heading text-ink-900 mb-1">{typeof last === "number" ? last : "—"}{suffix}</div>
      <div className="h-16 -mx-1">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 4, right: 4, left: 4, bottom: 0 }}>
            <XAxis dataKey="session_num" hide />
            <YAxis hide domain={["dataMin - 0.5", "dataMax + 0.5"]} />
            <Tooltip
              cursor={{ stroke: "#d8d2c4" }}
              contentStyle={{ background: "#fff", border: "1px solid #e8e3d6", borderRadius: 8, fontSize: 12 }}
              formatter={(v) => [`${v}${suffix}`, label]}
              labelFormatter={(_, payload) => payload?.[0]?.payload?.date || ""}
            />
            <Line
              type="monotone"
              dataKey={yKey}
              stroke={color}
              strokeWidth={2.2}
              dot={{ r: 3, fill: color, stroke: "white", strokeWidth: 1 }}
              activeDot={{ r: 5 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function SessionNode({ session, onOpen }) {
  const dotCls = COLOR_DOT[session.color] || COLOR_DOT.gray;
  return (
    <li className="relative pl-14" data-testid={`timeline-node-${session.session_num}`}>
      {/* Vertical rail */}
      <span className="absolute left-5 top-0 bottom-0 w-px bg-paper-200" aria-hidden />
      <div className={`absolute left-2 top-3 w-7 h-7 rounded-full ${dotCls} ring-4 flex items-center justify-center text-[11px] font-medium`}>
        {session.session_num}
      </div>
      <button
        type="button"
        onClick={() => onOpen(session)}
        data-testid={`timeline-open-${session.session_num}`}
        className="w-full text-left bg-white border border-paper-200 hover:border-sage-500/40 hover:shadow-sm transition rounded-2xl p-4 sm:p-5"
      >
        <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-ink-500">{COLOR_LABEL[session.color] || "Session"}</div>
            <div className="text-base font-medium text-ink-900">{session.date} · Session {session.session_num}</div>
          </div>
          <ChevronRight className="w-5 h-5 text-ink-400 shrink-0" />
        </div>
        {session.headline && <p className="text-sm text-ink-700 mb-3 italic">&ldquo;{session.headline}&rdquo;</p>}
        {!session.headline && session.intake_notes && (
          <p className="text-sm text-ink-700 mb-3 line-clamp-2">{session.intake_notes}</p>
        )}
        <div className="flex items-center gap-1.5 flex-wrap text-xs">
          {session.pain_before != null && session.pain_after != null && (
            <span className="px-2 py-0.5 rounded-full bg-paper-100 text-ink-700">Pain {session.pain_before}→{session.pain_after}/10</span>
          )}
          {session.rom_delta_pct != null && (
            <span className={`px-2 py-0.5 rounded-full ${session.rom_delta_pct > 0 ? "bg-sage-500/10 text-sage-700" : "bg-paper-100 text-ink-700"}`}>
              ROM {session.rom_delta_pct > 0 ? "+" : ""}{session.rom_delta_pct}%
            </span>
          )}
          {session.posture_score != null && (
            <span className="px-2 py-0.5 rounded-full bg-paper-100 text-ink-700">Posture {session.posture_score}/100</span>
          )}
          {session.worked_areas_count > 0 && (
            <span className="px-2 py-0.5 rounded-full bg-paper-100 text-ink-700 inline-flex items-center gap-1"><Activity className="w-3 h-3" />{session.worked_areas_count}</span>
          )}
          {session.captures_count > 0 && (
            <span className="px-2 py-0.5 rounded-full bg-paper-100 text-ink-700 inline-flex items-center gap-1"><Mic className="w-3 h-3" />{session.captures_count}</span>
          )}
          {session.photo_count > 0 && (
            <span className="px-2 py-0.5 rounded-full bg-paper-100 text-ink-700 inline-flex items-center gap-1"><Camera className="w-3 h-3" />{session.photo_count}</span>
          )}
        </div>
      </button>
    </li>
  );
}

function SessionDrawer({ session, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!session) return undefined;
    let cancelled = false;
    Promise.resolve().then(async () => {
      if (cancelled) return;
      setLoading(true);
      try {
        const r = await api.get(`/sessions/${session.id}`);
        if (!cancelled) setDetail(r.data);
      } catch {
        if (!cancelled) toast.error("Could not load session.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, [session]);

  if (!session) return null;

  const photos = detail?.photos || {};
  const soap = detail?.soap || {};
  const outcome = detail?.outcome || {};
  const captures = detail?.clinical_capture || [];
  const workedAreas = detail?.worked_areas || [];

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="timeline-drawer">
      <div className="absolute inset-0 bg-ink-900/40 backdrop-blur-sm" onClick={onClose} aria-hidden />
      <div className="relative w-full sm:max-w-2xl bg-paper-50 shadow-2xl flex flex-col">
        <div className="sticky top-0 bg-paper-50/95 backdrop-blur border-b border-paper-200 px-5 sm:px-7 py-4 flex items-center justify-between z-10">
          <div>
            <div className="label-overline">{COLOR_LABEL[session.color] || "Session"}</div>
            <h2 className="font-heading text-xl text-ink-900">{session.date} · Session {session.session_num}</h2>
          </div>
          <button
            onClick={onClose}
            data-testid="timeline-drawer-close"
            className="w-11 h-11 rounded-full hover:bg-paper-100 inline-flex items-center justify-center"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 sm:px-7 py-6 space-y-6">
          {loading && <div className="py-8 text-center text-ink-500">Loading…</div>}
          {!loading && (
            <>
              {/* Stats strip */}
              <div className="grid grid-cols-3 gap-2">
                <Stat label="Pain" before={outcome.pain_before} after={outcome.pain_after} suffix="/10" lowerIsBetter />
                <Stat label="ROM avg" before={null} after={null} value={
                  (outcome.rom_measurements || []).length > 0 ? `${(outcome.rom_measurements || []).length} joints` : "—"
                } />
                <Stat label="Posture" value={session.posture_score != null ? `${session.posture_score}/100` : "—"} />
              </div>

              {/* Photos */}
              {(photos.front || photos.back || photos.side) && (
                <div>
                  <div className="label-overline mb-2">Photos</div>
                  <div className="grid grid-cols-3 gap-2" data-testid="drawer-photos">
                    {["front", "back", "side"].map((k) => {
                      const p = photos[k];
                      if (!p) return null;
                      const src = typeof p === "string" ? p : (p.url || p.path || (p.image_base64 ? `data:image/jpeg;base64,${p.image_base64}` : ""));
                      if (!src) return null;
                      return (
                        <a key={k} href={src} target="_blank" rel="noopener noreferrer" className="block bg-paper-100 rounded-xl overflow-hidden aspect-[3/4]">
                          <img src={src} alt={`${k} view`} className="w-full h-full object-cover" loading="lazy" />
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Findings — clinical captures */}
              {captures.length > 0 && (
                <div>
                  <div className="label-overline mb-2 inline-flex items-center gap-1.5"><Mic className="w-3.5 h-3.5" /> Findings · {captures.length}</div>
                  <ul className="space-y-2" data-testid="drawer-findings">
                    {captures.map((c, i) => (
                      <li key={i} className="bg-white border border-paper-200 rounded-xl px-3 py-2 text-sm">
                        <div className="text-ink-900 font-medium">{c.side ? c.side[0].toUpperCase() + c.side.slice(1) + " " : ""}{c.body_region || "—"}{c.severity != null ? ` · ${c.severity}/10` : ""}</div>
                        {c.tissue_quality && <div className="text-xs text-ink-600">{c.tissue_quality}</div>}
                        {c.client_response && <div className="text-xs text-ink-500 italic mt-1">→ {c.client_response}</div>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Worked areas */}
              {workedAreas.length > 0 && (
                <div>
                  <div className="label-overline mb-2">Worked areas · {workedAreas.length}</div>
                  <div className="flex flex-wrap gap-1.5">
                    {workedAreas.map((a) => (
                      <span key={a} className="text-xs px-2 py-1 rounded-full bg-sage-50 text-sage-700 border border-sage-100">{a}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* SOAP */}
              {(soap.subjective || soap.objective || soap.assessment || soap.plan) && (
                <div>
                  <div className="label-overline mb-2 inline-flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" /> Treatment notes</div>
                  <div className="bg-white border border-paper-200 rounded-2xl divide-y divide-paper-200" data-testid="drawer-soap">
                    {["subjective", "objective", "assessment", "plan"].map((k) => (
                      soap[k] ? (
                        <div key={k} className="p-4">
                          <div className="text-[10px] uppercase tracking-[0.18em] text-ink-500 mb-1">{k.charAt(0).toUpperCase()} — {k.charAt(0).toUpperCase() + k.slice(1)}</div>
                          <p className="text-sm text-ink-700 whitespace-pre-wrap">{soap[k]}</p>
                        </div>
                      ) : null
                    ))}
                  </div>
                </div>
              )}

              {/* Outcome */}
              {outcome.impact_summary?.body_paragraph && (
                <div>
                  <div className="label-overline mb-2 inline-flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> Outcome</div>
                  <p className="text-sm text-ink-700 italic">&ldquo;{outcome.impact_summary.body_paragraph}&rdquo;</p>
                </div>
              )}

              <Link
                to={`/clients/${detail?.client_id}/sessions/${session.id}`}
                data-testid="drawer-open-full-session"
                className="block w-full text-center min-h-[48px] px-6 rounded-full border border-paper-200 hover:bg-paper-100 inline-flex items-center justify-center gap-2 text-sm text-ink-700"
              >
                Open full session <ChevronRight className="w-4 h-4" />
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, before, after, value, suffix = "", lowerIsBetter = false }) {
  const display = value !== undefined ? value : (after != null ? `${after}${suffix}` : "—");
  let delta = null;
  if (before != null && after != null) {
    delta = after - before;
  }
  const isImprovement = delta !== null && (lowerIsBetter ? delta < 0 : delta > 0);
  return (
    <div className="bg-white border border-paper-200 rounded-xl px-3 py-2">
      <div className="text-[10px] uppercase tracking-[0.16em] text-ink-500">{label}</div>
      <div className="text-base font-medium text-ink-900">{display}</div>
      {delta !== null && (
        <div className={`text-xs ${isImprovement ? "text-sage-700" : "text-rose-600"}`}>
          {before}{suffix} → {after}{suffix}
        </div>
      )}
    </div>
  );
}

export default function ClientTimeline() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openSession, setOpenSession] = useState(null);

  useEffect(() => {
    let cancelled = false;
    Promise.resolve().then(async () => {
      if (cancelled) return;
      try {
        const r = await api.get(`/clients/${clientId}/timeline`);
        if (!cancelled) setData(r.data);
      } catch {
        if (!cancelled) toast.error("Could not load timeline.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, [clientId]);

  const doc = data?.documentation || {};
  const sessions = data?.sessions || [];

  const improvementColor = useMemo(() => {
    const p = doc.improvement_pct;
    if (p == null) return "text-ink-500";
    if (p >= 30) return "text-sage-700";
    if (p > 0) return "text-amber-600";
    return "text-rose-600";
  }, [doc.improvement_pct]);

  return (
    <Layout>
      <div className="max-w-5xl mx-auto">
        <button
          onClick={() => navigate(`/clients/${clientId}`)}
          className="text-sm text-ink-500 hover:text-ink-900 inline-flex items-center gap-1.5 mb-4"
          data-testid="timeline-back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Back to client
        </button>

        <div className="mb-2 label-overline">Clinical timeline</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-light tracking-tight text-ink-900 mb-1">
          {doc.client_name || "Client"} — Progress over time
        </h1>
        {!loading && (
          <p className="text-sm text-ink-600 mb-8" data-testid="timeline-meta">
            {doc.session_count || 0} sessions{doc.date_of_first_session ? ` · since ${doc.date_of_first_session}` : ""}
          </p>
        )}

        {loading && <div className="py-20 text-center text-ink-500">Loading timeline…</div>}

        {!loading && sessions.length === 0 && (
          <div className="bg-white border border-paper-200 rounded-3xl p-8 text-center" data-testid="timeline-empty">
            <h2 className="font-heading text-xl text-ink-900 mb-2">No completed sessions yet.</h2>
            <p className="text-ink-600 mb-4">As you complete sessions, the timeline will fill in with pain/ROM/posture trends and a clickable history.</p>
          </div>
        )}

        {!loading && sessions.length > 0 && (
          <>
            {/* Trend strip */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8" data-testid="timeline-trends">
              <MiniTrend
                data={data.pain_trend}
                color="#c84931"
                label="Pain"
                suffix="/10"
                yKey="pain"
                invertColor
              />
              <MiniTrend
                data={data.rom_trend}
                color="#5b6b5f"
                label="ROM"
                suffix="°"
                yKey="rom"
              />
              <MiniTrend
                data={data.posture_trend}
                color="#23362a"
                label="Posture"
                suffix="/100"
                yKey="posture"
              />
            </div>

            {/* LOP / documentation summary */}
            <div className="bg-white border border-paper-200 rounded-3xl p-5 sm:p-6 mb-8" data-testid="timeline-documentation">
              <div className="label-overline mb-2">Documentation summary</div>
              <h2 className="font-heading text-xl text-ink-900 mb-4">For LOP &amp; chronic-pain records</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
                <Kpi label="Sessions" value={doc.session_count || 0} />
                <Kpi label="Baseline pain" value={doc.baseline_pain != null ? `${doc.baseline_pain}/10` : "—"} />
                <Kpi label="Current pain" value={doc.latest_pain != null ? `${doc.latest_pain}/10` : "—"} />
                <Kpi label="Overall improvement" value={doc.improvement_pct != null ? `${doc.improvement_pct}%` : "—"} valueClassName={improvementColor} />
              </div>
              {(doc.persistent_findings || []).length > 0 && (
                <div className="mb-4">
                  <div className="text-xs text-ink-500 mb-1.5">Persistent areas treated (across ≥2 sessions)</div>
                  <div className="flex flex-wrap gap-1.5">
                    {doc.persistent_findings.map((p) => (
                      <span key={p.area} className="text-xs px-2 py-1 rounded-full bg-sage-50 text-sage-700 border border-sage-100">
                        {p.area} · {p.count}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {doc.recommended_frequency && (
                <div className="text-sm text-ink-700 inline-flex items-center gap-1.5">
                  <Clock className="w-4 h-4 text-ink-400" />
                  Average treatment cadence: <span className="font-medium">{doc.recommended_frequency}</span>
                </div>
              )}
            </div>

            {/* Timeline list */}
            <div className="mb-2 label-overline inline-flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> Session by session</div>
            <ol className="space-y-3" data-testid="timeline-list">
              {[...sessions].reverse().map((s) => (
                <SessionNode key={s.id} session={s} onOpen={setOpenSession} />
              ))}
            </ol>
          </>
        )}

        <SessionDrawer session={openSession} onClose={() => setOpenSession(null)} />
      </div>
    </Layout>
  );
}

function Kpi({ label, value, valueClassName = "" }) {
  return (
    <div className="bg-paper-50 border border-paper-200 rounded-2xl px-3 py-2.5">
      <div className="text-[10px] uppercase tracking-[0.16em] text-ink-500">{label}</div>
      <div className={`text-lg font-medium text-ink-900 ${valueClassName}`}>{value}</div>
    </div>
  );
}
